import { hasPageHandle } from '../../react-router/pageHandle.utils';
import { LAUNCH_DARKLY_PAGE_NAMES } from '../../react-router/withLaunchDarkly';

import type { PatientPortraitIdentity } from './PatientPortraitLifecycleModel';
import type { Params } from 'react-router';

export interface PatientPortraitRouteMatch {
	readonly handle: unknown;
	readonly params: Params<string>;
}

// The page handle every portrait route already declares is what makes this infra work for both page
// families without either of them opting in: the appointment-based routes in `App.tsx` and the
// patient-based routes in `PatientPortraitRoutes.tsx` are all marked, overlays and embedded variants
// included. The page names themselves still say "Summary" because LaunchDarkly targets them by that
// string - renaming them there is a separate, coordinated change.
const PATIENT_PORTRAIT_PAGE_NAMES: ReadonlySet<string> = new Set([
	LAUNCH_DARKLY_PAGE_NAMES.appointmentBasedSummary,
	LAUNCH_DARKLY_PAGE_NAMES.patientBasedSummary,
	LAUNCH_DARKLY_PAGE_NAMES.patientBasedSummaryWithAppointment,
]);

function isPatientPortraitPageName(pageName: string): boolean {
	return PATIENT_PORTRAIT_PAGE_NAMES.has(pageName);
}

export function getMaybeOpenPatientPortrait(
	matches: ReadonlyArray<PatientPortraitRouteMatch>,
): PatientPortraitIdentity | null {
	const maybeDeepestMatchWithPageHandle = matches.findLast(hasPageHandle);

	if (!maybeDeepestMatchWithPageHandle || !isPatientPortraitPageName(maybeDeepestMatchWithPageHandle.handle.page)) {
		return null;
	}

	const { navinaPatientId: maybeNavinaPatientId, navinaAppointmentId: maybeNavinaAppointmentId } =
		maybeDeepestMatchWithPageHandle.params;

	// A portrait route without either id is not an open portrait - the "we could not match this
	// appointment" page is a portrait route with nothing behind it.
	if (!maybeNavinaPatientId && !maybeNavinaAppointmentId) {
		return null;
	}

	return {
		maybeNavinaPatientId: maybeNavinaPatientId ?? null,
		maybeNavinaAppointmentId: maybeNavinaAppointmentId ?? null,
	};
}
